/**
 * Frontier Rankings — five sortable leaderboards over Epoch's notable-models
 * dataset. By compute, params, recency, lab volume, and country share.
 */
import { useEffect, useState } from 'react'
import { Trophy, RefreshCw, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface ModelRow {
  model: string
  organization: string
  publication_date: string | null
  parameters: number | null
  training_compute: number | null
  country: string | null
  reference: string | null
}

interface LabRow { lab: string; models: number }
interface CountryRow { country: string; models: number }

interface Payload {
  slice: string
  total: number
  by_compute: ModelRow[]
  by_params: ModelRow[]
  by_recency: ModelRow[]
  by_lab: LabRow[]
  by_country: CountryRow[]
  fetched_at: string
}

function formatBig(n: number | null | undefined): string {
  if (!n) return '—'
  if (n >= 1e24) return `${(n / 1e24).toFixed(1)}Y`
  if (n >= 1e21) return `${(n / 1e21).toFixed(1)}Z`
  if (n >= 1e18) return `${(n / 1e18).toFixed(1)}E`
  if (n >= 1e15) return `${(n / 1e15).toFixed(1)}P`
  if (n >= 1e12) return `${(n / 1e12).toFixed(1)}T`
  if (n >= 1e9)  return `${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6)  return `${(n / 1e6).toFixed(1)}M`
  return n.toLocaleString()
}

function rankColor(rank: number): string {
  if (rank === 1) return 'text-amber bg-amber/15 border-amber/40'
  if (rank === 2) return 'text-text-secondary bg-surface-raised border-border'
  if (rank === 3) return 'text-orange bg-orange/15 border-orange/40'
  return 'text-text-muted bg-surface-raised border-border'
}

export default function FrontierRankingsPage() {
  const [slice, setSlice] = useState<'frontier' | 'notable'>('frontier')
  const [data, setData] = useState<Payload | null>(null)
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    api.get('/api/frontier/rankings', { params: { slice, limit: 50 } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [slice])  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Trophy size={18} className="text-amber" /> FRONTIER RANKINGS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          who leads · by compute / params / recency / volume
        </span>
        <select className="input text-xs" value={slice}
          onChange={e => setSlice(e.target.value as 'frontier' | 'notable')}
          aria-label="Slice">
          <option value="frontier">Frontier (&gt;10²³ FLOP)</option>
          <option value="notable">Notable (curated)</option>
        </select>
        {data && (
          <span className="text-[10px] text-text-muted font-mono">
            {data.total.toLocaleString()} models · updated {data.fetched_at?.slice(0, 16).replace('T', ' ')} UTC
          </span>
        )}
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Top-of-leaderboard winners */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Winner label="Most training compute"
            model={data.by_compute[0]} value={formatBig(data.by_compute[0]?.training_compute)} unit="FLOP"
            tone="amber" />
          <Winner label="Most parameters"
            model={data.by_params[0]} value={formatBig(data.by_params[0]?.parameters)} unit="params"
            tone="purple" />
          <Winner label="Most recent"
            model={data.by_recency[0]} value={data.by_recency[0]?.publication_date || '—'} unit="published"
            tone="emerald" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <RankTable title="By training compute (FLOP)"
          rows={data?.by_compute || []} getValue={r => formatBig(r.training_compute)} unit="FLOP"
          tone="amber-light" />
        <RankTable title="By parameters"
          rows={data?.by_params || []} getValue={r => formatBig(r.parameters)} unit="params"
          tone="purple-light" />
      </div>

      <RankTable title="Most recent"
        rows={data?.by_recency || []} getValue={r => r.publication_date || '—'} unit="date"
        tone="emerald" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <section className="card p-3">
          <h2 className="text-sm font-semibold text-text-primary mb-2">Models per lab</h2>
          <div className="space-y-1">
            {(data?.by_lab || []).slice(0, 25).map((r, i) => (
              <div key={r.lab} className="flex items-center gap-2 text-xs">
                <span className={clsx('badge w-7 text-center text-[10px] border', rankColor(i + 1))}>
                  {i + 1}
                </span>
                <span className="flex-1 text-text-primary">{r.lab}</span>
                <span className="font-mono text-amber-light">{r.models}</span>
              </div>
            ))}
          </div>
        </section>
        <section className="card p-3">
          <h2 className="text-sm font-semibold text-text-primary mb-2">Models per country</h2>
          <div className="space-y-1">
            {(data?.by_country || []).slice(0, 25).map((r, i) => (
              <div key={r.country} className="flex items-center gap-2 text-xs">
                <span className={clsx('badge w-7 text-center text-[10px] border', rankColor(i + 1))}>
                  {i + 1}
                </span>
                <span className="flex-1 text-text-primary">{r.country}</span>
                <span className="font-mono text-emerald">{r.models}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Source: Epoch AI Notable Models dataset · cached 1h.
      </p>
    </div>
  )
}

function Winner({ label, model, value, unit, tone }: {
  label: string; model?: ModelRow; value: string; unit: string
  tone: 'amber' | 'purple' | 'emerald'
}) {
  const c = tone === 'amber' ? 'text-amber'
          : tone === 'purple' ? 'text-purple-light'
          : 'text-emerald'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">{label}</div>
      <div className="text-sm font-bold text-text-primary truncate">{model?.model || '—'}</div>
      <div className="text-[10px] text-text-muted truncate">{model?.organization}</div>
      <div className={clsx('text-2xl font-black font-mono mt-1', c)}>{value}</div>
      <div className="text-[9px] text-text-muted">{unit}</div>
    </div>
  )
}

function RankTable({ title, rows, getValue, unit, tone }: {
  title: string; rows: ModelRow[]
  getValue: (r: ModelRow) => string
  unit: string
  tone: string
}) {
  return (
    <section className="card p-0 overflow-hidden">
      <header className="px-3 py-2 border-b border-border bg-surface-raised">
        <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
      </header>
      <div className="max-h-[400px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-surface-raised z-10">
            <tr className="text-[9px] uppercase tracking-widest text-text-muted border-b border-border">
              <th className="text-left px-3 py-1.5 w-10">#</th>
              <th className="text-left px-3 py-1.5">Model</th>
              <th className="text-left px-3 py-1.5">Org</th>
              <th className="text-right px-3 py-1.5">{unit}</th>
              <th className="text-left px-3 py-1.5"><span className="sr-only">Reference</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.slice(0, 25).map((r, i) => (
              <tr key={`${r.model}-${i}`} className="hover:bg-surface-raised/40">
                <td className="px-3 py-1 font-mono text-text-muted">{i + 1}</td>
                <td className="px-3 py-1 font-semibold text-text-primary truncate">{r.model}</td>
                <td className="px-3 py-1 text-purple-light truncate">{r.organization}</td>
                <td className={clsx('px-3 py-1 text-right font-mono', `text-${tone}`)}>{getValue(r)}</td>
                <td className="px-3 py-1">
                  {r.reference && (
                    <a href={r.reference} target="_blank" rel="noreferrer" aria-label="Reference">
                      <ExternalLink size={10} className="text-blue-light" />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}
